// src/api/portalApi.js
//
// Every call the Operator Portal makes. ONE FILE, so the portal's network
// footprint is a thing you can read rather than something you assemble by
// grepping components.
//
// ── EVERY PATH HERE MUST BE REACHABLE BY THE PORTAL ROLE CLASS ──────────────
//
// middleware/restrictedAccount.js confines a plan_submitter to
// ALLOW_EXACT_PORTAL (/api/v1/auth/me, /api/v1/orgs/me) plus the
// /api/v1/portal/ prefix. Anything else 403s with `restricted_account` before
// it reaches a router.
//
// That is not a style rule, it is the boundary: a component that reaches for
// /api/v1/aois because it was convenient gets a 403 the user reads as a broken
// page. PortalShell.fetches.test.jsx measures it by rendering the real
// components and checking every recorded path against isAllowedPath(p,
// 'portal') -- the gate's own function, not a copy of its list.

import { fetchWithAuth } from "./fetchWithAuth";

const BASE = "/api/v1/portal/plan-reviews";

/**
 * A failed portal call carries a `code` the UI branches on -- see
 * docs/OPERATOR_PORTAL_DESIGN.md. Thrown rather than returned so a component
 * cannot forget to check, with the code attached rather than parsed out of a
 * message.
 */
export class PortalError extends Error {
  constructor(status, payload, { parsed = true } = {}) {
    super(payload?.error || `Request failed (${status})`);
    this.name = "PortalError";
    this.status = status;
    this.code = typeof payload?.code === "string" && payload.code.length > 0 ? payload.code : null;
    this.payload = payload ?? null;
    this.shape = shapeOfFailure(status, this.code, parsed);
  }
}

/**
 * WHICH OF THREE SHAPES THE FAILURE ARRIVED IN. A branch on `code` alone cannot
 * tell them apart, and the third one is not hypothetical:
 *
 *   "coded"     The app refused and said why. `code` is a string from
 *               docs/OPERATOR_PORTAL_DESIGN.md and the UI has copy for it.
 *
 *   "uncoded"   The app answered, in JSON, WITHOUT a code -- route 2's
 *               `{error: "Bad request", detail: "No file uploaded"}`, or a 500's
 *               `{error: "Failed to create plan review"}`. `error` is readable
 *               but it is not a branch point. THIS IS ITS OWN CASE rather than
 *               falling through with the ones above, because a component that
 *               writes `if (e.code === X) ... else <generic>` silently treats a
 *               real server fault as an unrecognised refusal.
 *
 *   "non_json"  NOTHING THE APP WROTE reached us. The body did not parse as
 *               JSON at all, which means something in front of Express answered:
 *               nginx, a proxy, a load balancer. The one we have actually
 *               measured is nginx's `client_max_body_size` returning an HTML 413
 *               (REM-376) -- the exact failure this release's size layering
 *               exists to keep off this path. Naming it means that if the
 *               layering ever regresses, the UI can say so instead of rendering
 *               "Request failed (413)" and leaving a submitter to guess.
 *
 * Named for what was OBSERVED, not for what it implies: "non_json" is a fact
 * about the body. Calling it "gateway" would be a guess -- an Express 500 with
 * an HTML stack page lands here too.
 */
function shapeOfFailure(_status, code, parsed) {
  if (!parsed) return "non_json";
  return code ? "coded" : "uncoded";
}

async function call(url, options) {
  const res = await fetchWithAuth(url, options);
  let payload = null;
  let parsed = true;
  // A 204 has no body and `.json()` throws on it, same as HTML would. It is only
  // reachable on the ok path, where `parsed` is never read -- but tracked
  // honestly rather than left to that coincidence.
  try { payload = await res.json(); } catch { parsed = false; }
  if (!res.ok) throw new PortalError(res.status, payload, { parsed });
  return payload;
}

const json = (method, body) => ({
  method,
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(body),
});

/**
 * Route 5. `scope` comes back 'submitter' or 'org' — the server decides, not us.
 *
 * Also carries `awaiting_statuses`: the statuses this caller may WRITE at,
 * computed server-side from mayWritePlanReview over every status the machine
 * has. The client filters rows with it rather than restating them, so the
 * rollup's count and the queue beneath it cannot disagree, and a change to
 * PLAN_REVIEW_WRITER moves both.
 */
export function listPlanReviews({ status } = {}) {
  const q = status ? `?status=${encodeURIComponent(status)}` : "";
  return call(`${BASE}${q}`);
}

/** Route 6. Carries `viewer_role`, which is how the page knows which side it is on. */
export function getPlanReview(id) {
  return call(`${BASE}/${id}`);
}

/** Route 7. Polled; there is no SSE for the portal (§3.12b). */
export function getThread(id, { limit, before } = {}) {
  const p = new URLSearchParams();
  if (limit) p.set("limit", String(limit));
  if (before) p.set("before", before);
  // Built without a NESTED template literal on purpose. gen-ui-endpoints.mjs
  // resolves call sites statically and reported this one as "interpolation
  // contains a nested template literal; path not fully resolvable" -- which
  // means the route-inventory cross-check cannot see the endpoint at all, and a
  // portal route could be removed server-side without that check noticing.
  // One extra line buys the scanner a resolvable path.
  // THE PATH IS ITS OWN TEMPLATE, and the query is concatenated outside it.
  // gen-ui-endpoints.mjs resolves call sites statically: a nested template gives
  // it "not fully resolvable", and interpolating the query INTO the path gives
  // it `/thread{param}`, which matches no row in ROUTE_INVENTORY.md and so is
  // reported as an endpoint the server does not serve. Either way its
  // cross-check stops covering this route. Split, it resolves to
  // /api/v1/portal/plan-reviews/{id}/thread and the check works.
  const q = p.toString();
  const path = `${BASE}/${id}/thread`;
  return call(q ? path + "?" + q : path);
}

/** Route 8. */
export function addNote(id, { body, parentId = null }) {
  return call(`${BASE}/${id}/notes`, json("POST", { body, parent_id: parentId }));
}

// ── WHEN A MARKUP WRITER IS ADDED HERE, IT MUST RE-READ FROM ROUTE 6 ────────
//
// Route 9's 201 echoes the four imagery-witness columns its RETURNING clause
// selects -- imagery_dataset_key, imagery_acquisition_date, imagery_lod,
// imagery_resolved_at -- and route 6's MARKUP_KEYS deliberately excludes all
// four, because decision 16 says the witness is an audit fact with no UI.
//
// So a component that renders route 9's response DIRECTLY puts the witness on
// screen, and then a submitter arguing about which capture date a setback was
// judged against is arguing with a field we put in front of them.
//
// THE CREATE RESPONSE IS A SUCCESS SIGNAL, NOT A DATA SOURCE. Re-read the
// review. Route 8's note writer already does the other half of this -- its 201
// goes through the same shaper as the thread read, so there is one shape rather
// than two -- and route 9 is the one where they differ.
//
// There is no markup writer here yet, so the rule is satisfied by construction
// today. It is written where whoever adds one will be reading.

// ── THE UPLOAD FLOW: 2 -> 3 -> 4, AND WHY IT IS THREE CALLS ────────────────
//
// inspect stages the file and lists its sublayers; preview converts ONE of them
// and hands back the features that submit would store; submit writes the
// revision. The upload_token threads all three, and it is scoped to the review,
// so a token from review A is refused (403 token_scope_mismatch) rather than
// converted into review B's revision.
//
// The token also EXPIRES. Every one of these can answer `token_expired`, at any
// step, including between preview and submit while the submitter reads the map.
// A component that handles it only on the first call will strand them.

/**
 * Route 2. Stage a file and list what is in it.
 *
 * ── NO Content-Type HEADER. DELIBERATELY. ──────────────────────────────────
 *
 * The `json()` helper above sets `Content-Type: application/json`, and every
 * other writer here uses it. This one MUST NOT, and not as a matter of taste:
 * a multipart body is only parseable with the boundary string, which the
 * browser generates and appends to the header when it -- and only it -- sets
 * the header for a FormData body. Set the header by hand, with or without a
 * boundary of your own invention, and the browser leaves it alone; multer then
 * reads a header whose boundary does not occur in the body, finds no file part,
 * and the route answers `{error: "Bad request", detail: "No file uploaded"}`
 * -- a 400 that says the file is missing about a request that carried it.
 *
 * So: pass the FormData, pass NO headers, and let fetch do it.
 *
 * @param {string} id
 * @param {File|Blob} file
 */
export function inspectUpload(id, file) {
  const form = new FormData();
  // The field name is multer's `upload.single("file")`. A different name is
  // LIMIT_UNEXPECTED_FILE, which the server now answers as
  // `upload_form_invalid` rather than a 500.
  form.append("file", file);
  return call(`${BASE}/${id}/inspect`, { method: "POST", body: form });
}

/**
 * Route 3. Convert ONE sublayer and return the features submit would store.
 *
 * The features come back already prepared -- same ordinals, same geom_class,
 * same labelOf output as route 4 will persist -- so what the map draws is what
 * gets submitted, rather than a separate rendering that can disagree with it.
 */
export function previewSublayer(id, { uploadToken, sublayerIndex }) {
  return call(`${BASE}/${id}/preview`,
    json("POST", { upload_token: uploadToken, sublayer_index: sublayerIndex }));
}

/**
 * Route 4. Write the revision.
 *
 * ── `tags` IS AN ARRAY OF {ordinal, tag}. IT IS NOT KEYED BY ORDINAL. ───────
 *
 * This comment said "keyed by ordinal" for three commits and it was wrong, and
 * wrong in the direction that costs most: the panel held `useState({})`, and an
 * object is refused by validateTags before it looks at a single entry.
 *
 *   {}                      -> 400 tags_missing
 *   {"0":"pad","1":"road"}  -> 400 tags_missing     <- identical
 *
 * So a tagging control written against this comment would have produced the
 * same refusal as no control at all.
 *
 * ORDINALS ARE THE SERVER'S, ZERO-BASED, AND INTEGERS. `prepareFeatures`
 * numbers from 0; `Object.entries()` stringifies keys and yields
 * `tag_ordinal_invalid`; numbering from 1 yields `tag_unknown_feature` with a
 * message naming only the LAST ordinal, because the earlier ones are valid.
 *
 * Build the payload with `tagsForWire` from portal/portalTags.js, which is the
 * only producer of it and takes every ordinal from route 3's own response.
 */
export function submitRevision(id, { uploadToken, sublayerIndex, tags }) {
  return call(`${BASE}/${id}/submit`,
    json("POST", { upload_token: uploadToken, sublayer_index: sublayerIndex, tags }));
}

/**
 * Route 9. Record a finding against one submitted feature.
 *
 * ── `geom` IS OPTIONAL, AND null IS THE ORDINARY CASE ───────────────────────
 *
 * checkMarkupInput says so outright: "most findings are about the feature as
 * submitted". A finding is a TYPE plus a NOTE against a feature_id; the
 * reviewer's own geometry is an annotation on top, never a mutation of the
 * submitter's shape, which migration 113 makes immutable.
 *
 * That is why the finding form works with no drawing at all, and why drawing is
 * the LAST thing this phase builds rather than the first.
 *
 * `imagery_hint` is deliberately not sent. The server resolves the witness from
 * the feature geometry and records `imagery_hint_honoured: false`, which is
 * honest — we did not offer one. See §3.25.
 *
 * THE 201 IS A SUCCESS SIGNAL, NOT A DATA SOURCE. It echoes the four witness
 * columns route 6's MARKUP_KEYS deliberately withholds (decision 16: the
 * witness is an audit fact with no UI), so rendering it directly would put the
 * capture date in front of a reviewer arguing about it. Re-read route 6.
 */
export function createMarkup(id, { featureId, markupType, note, geom = null }) {
  return call(`${BASE}/${id}/markup`,
    json("POST", { feature_id: featureId, markup_type: markupType, note, geom }));
}

/**
 * Route 11. Return the plan to the submitter with the findings on it.
 *
 * ── NO BODY. AND IT IS THE MOMENT OF DISCLOSURE. ───────────────────────────
 *
 * A single POST with no parameters. What it does is not small: it stamps
 * `frozen_at` on every open finding on the current revision, and FROZEN MEANS
 * SENT. Until this call the submitter cannot see any of them (§3.1); after it
 * they can see all of them, and migration 113 makes them immutable.
 *
 * So the 200 carries `markup_frozen` — how many were just disclosed — and the
 * UI says it. A reviewer who returns a plan should be told what left their
 * hands, at the moment it leaves.
 *
 * Refuses `nothing_to_return` when the revision carries no findings at all:
 * "A returned plan must carry at least one finding. Add markup, or approve it."
 * The client knows that from `review.markup.length` and disables rather than
 * letting the round trip explain — §3.19's rule for the layer buttons.
 */
export function returnPlanReview(id) {
  return call(`${BASE}/${id}/return`, json("POST", {}));
}

/**
 * Route 12. Approve the plan. Terminal — there is no reopen in v1.
 *
 * ── THE BUTTON IS NOT OFFERED UNLESS route 6 SAYS IT CAN BE ────────────────
 *
 * requireOrgAdmin gates this, and its refusal is a bare
 * `403 {"error":"Forbidden"}` with NO CODE — the one refusal in this feature
 * with nothing behind it. In the disposition taxonomy that is `unattributed`,
 * which renders "Something went wrong, and we can't tell you what" for a
 * permission the server knows the answer to.
 *
 * So route 6 answers `viewer_may_approve`, computed by authz.js's
 * canActAsOrgAdmin — the same composition requireOrgAdmin applies — and the UI
 * does not offer a control it knows will fail.
 */
export function approvePlanReview(id) {
  return call(`${BASE}/${id}/approve`, json("POST", {}));
}

/**
 * Route 1.
 *
 * `ownerOrgId` is the org the plan review is FOR. Omitted means the caller's
 * own org -- the intra-org review -- and route 1 refuses the omission with
 * `owner_org_id_required` when the caller's org has ever been named as an
 * operator, because for those callers the default is a guess.
 */
export function createPlanReview({ title, ownerOrgId = null }) {
  // The key is omitted rather than sent as null. Route 1 treats undefined and
  // null identically, so this is presentation -- but `{"owner_org_id": null}`
  // in a request log reads as a client that tried to name an owner and failed,
  // and the absent key reads as what it is.
  return call(BASE, json("POST", ownerOrgId ? { title, owner_org_id: ownerOrgId } : { title }));
}

/**
 * Route 4b. The owners who accept submissions from the caller's org.
 *
 * ── THIS IS THE WHOLE DIRECTORY, AND THAT IS THE POINT ─────────────────────
 *
 * The route filters `operator_org_id = caller`, so what comes back is grants
 * issued TO this org, never the ones it issued. An operator cannot enumerate
 * owners or discover that one exists; it learns which owners named it. The UI
 * must not widen that -- a picker built from any other source would be a
 * directory the route deliberately is not.
 *
 * Revoked grants come back MARKED (`live: false`), not filtered, so the picker
 * can distinguish "no longer accepts submissions from you" from "never did".
 */
export function listGrants() {
  return call("/api/v1/portal/grants");
}

/** Route 13. Returns a Blob, not JSON — the response IS the document. */
export async function exportRevision(id, revision, { format = "geojson" } = {}) {
  const res = await fetchWithAuth(`${BASE}/${id}/revisions/${revision}/export?format=${format}`);
  if (!res.ok) {
    let payload = null;
    let parsed = true;
    try { payload = await res.json(); } catch { parsed = false; }
    throw new PortalError(res.status, payload, { parsed });
  }
  return {
    blob: await res.blob(),
    // The server names the file; the client does not invent one. That name
    // carries no date by design -- planReviewExport.js mechanism 5.
    filename: (res.headers.get("Content-Disposition") || "").match(/filename="([^"]+)"/)?.[1]
      ?? `plan-review-rev${revision}.${format}`,
  };
}
